import { useEffect, useState } from "react";
import TablePagination from "../../../components/TablePagination";
import api from "../../../services/api";

import "react-toastify/dist/ReactToastify.css";

import { displayDateTime } from "../../../utils/masks";
import IHistDTO from "../../../models/IHistDTO";
import { StatusType } from "../../../models/enums/StatusType";
import { TypeHistory } from "../../../models/enums/TypeHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import AuditDetail from "../audit-detail/AuditDetail";
import { Link } from "react-router-dom";
import moment from "../../../utils/momentConfig";
import ILetterDetailDTO from "../../../models/ILetterDetailDTO";

export default function AuditList() {
  const [refresh, setRefresh] = useState<boolean>();
  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const [member, setMember] = useState();
  const [idMember, setIdMember] = useState(0);
  const [termSearch, setTermSearch] = useState<string>('');
  const [delayedSearchTerm, setDelayedSearchTerm] = useState('');
  const [reason, setReason] = useState('');
  
  const columns = [
    {
      name: "Histórico",
      grow: 2,
      cell: (row: IHistDTO) => (
        <div className="uk-flex uk-flex-column">
          <label>{row.memberName}</label>
          <br/>
          <label className={row.status == StatusType.INACTIVE ? 'uk-text-danger' : ''}>
            <strong>{row.reason}</strong>
          </label>
        </div>
      )
    },
    {
      name: "Obs.",
      cell: (row: IHistDTO) => (
        [TypeHistory.CHANGE_RECORDS].indexOf(row.reason) < 0 ? (
          <label 
            className={`uk-text-xsmall ${row.status == StatusType.INACTIVE ? 'uk-text-danger' : ''}`}
          >
            {handleShowNote(row)}
          </label>
        ) : (
          <a href="#" onClick={(ev)=> { ev.preventDefault(); handleDetail(row); }}
            uk-tooltip="title: Ver Diferenças"            
          >
            <FontAwesomeIcon icon={faEye} />
          </a>
        )
      )
    },
    {
      name: "Realizado",
      grow: 2,
      cell: (row: IHistDTO) => (
        <div className="uk-flex uk-flex-column uk-text-xsmall uk-text-light">
          <label className={row.status == StatusType.INACTIVE ? 'uk-text-danger' : ''}>
            Por: {row.createdBy}<br/>
            Em: {displayDateTime(row.createdDate)}
          </label>
          {
            row.status == StatusType.INACTIVE && (
              <label className="uk-text-danger">
                <strong>Cancelado em: {displayDateTime(row.modifiedDate)}</strong>
                <br/>
                Por: {row.modifiedBy}
              </label>
            )
          }
          {
            (row.status == StatusType.ACTIVE && row.historyClosedOn) && (
              <label className="uk-text-success">
                <strong>Entregue em: {displayDateTime(row.modifiedDate)}</strong>
                <br/>
                Por: {row.modifiedBy}
              </label>
            )
          }
        </div>
      )
    }
  ];

  const fetchData = async (page: number, limit: number) => {
    try {
      let params = {
        page: page,
        limit: limit,
        filterValue: delayedSearchTerm,
        reason: reason
      } as any;

      const response = await api.get(`audit/list`, { params: params });

      return response;
    } catch {
      return { data: [], total: 0 };
    }
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm, reason]);

  const afterCloseModal = () => {
    setOpenDetail(false);
    setMember(undefined);
    setIdMember(0);
  }

  const handleDetail = (row: any) => {
    setMember(row.note);
    setIdMember(row.idMember);
    setOpenDetail(true);
  }

  const handleShowNote = (row: IHistDTO) => {
    if (![TypeHistory.CHANGE_LETTER, TypeHistory.RECOMMENDATION_LETTER].includes(row.reason)) {
      return row.note;
    }

    if (row.historyClosedOn?.id) {
      return <div className="uk-text-italic">Carta de Mudança assinada em {moment(row.modifiedDate).format('DD/MM/yyyy HH:mm:ss')}</div>
    }

    if (row.status === StatusType.INACTIVE) {
      return <div className="uk-text-danger">Carta Cancelada</div>
    }

    let note = row.note;

    if (!note) return;
  
    if (note.startsWith('{') && note.endsWith('}')) {
      const noteObj = JSON.parse(note) as ILetterDetailDTO;
      note = noteObj.formatedNote ?? '';
    }

    if (note?.includes('-')) {
      const lines = note.split('-');
      return <div className="uk-margin-small-top">{lines.map(l => <><span>{l}</span><br/><br/></>)}</div>
    }

    return note;
  }

  return (
    <div id="page-audit">
      <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-bottom">
        <div className="uk-card-header">
          <div className="uk-grid-small uk-flex-middle" uk-grid="true">
            <div className="uk-width-expand">
              <h3 className="uk-card-title uk-margin-remove-bottom">
                Auditoria de Registros
              </h3>
            </div>
            <Link
              className="uk-button uk-button-primary uk-button-small"
              to={"/sistema/usuarios"}
            >
              Voltar
            </Link>
          </div>
        </div>
        <div className="uk-card-body">
          <div className="uk-grid-small uk-margin-top" uk-grid="true">
            <div className="uk-search uk-search-default uk-width-1-2@m">
              <span
                className="uk-search-icon-flip"
                uk-search-icon="true"
              ></span>
              <input
                className="uk-search-input uk-input uk-form-small"
                type="search"
                placeholder="Pesquise por nome..."
                style={{ textTransform: "uppercase" }}
                onChange={(ev) => {
                  setTermSearch(ev.target.value);
                }}
              />
            </div>
            <div className="uk-search uk-search-default uk-width-1-2@m">
              <div className="uk-flex uk-flex-middle">                
                <label className="uk-form-label uk-margin-small-right" htmlFor="year">Histórico</label>
                <div className="uk-form-controls">
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                      id="reason"
                      name="reason"
                      onChange={(ev) => setReason(ev.target.value)}
                    >       
                      <option value="">Filtre por Histórico...</option>
                      {Object.entries(TypeHistory)
                        .sort((a, b) => a[1].localeCompare(b[1]))
                        .map(([key, value]) => (
                        <option key={key} value={value}>
                          {value}
                        </option>
                      ))}
                      <option value={TypeHistory.ADD}>{TypeHistory.ADD.toString()}</option>
                    </select>
                    <button 
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>          
          <TablePagination
            columns={columns}
            fetchData={fetchData}
            refresh={refresh}
          />
        </div>
      </div>
      <AuditDetail 
        open={openDetail} 
        memberChanged={member}
        idMember={idMember}
        afterCloseModal={afterCloseModal}
      />
    </div>
  );
}
